/**
 * 战场粒子：枪口火、弹道火花、爆炸、扬尘、法则飘尘。
 * 全部是渲染层装饰，不进入模拟。
 */

import { Container, Sprite, Texture } from 'pixi.js';

interface P {
  spr: Sprite;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  size: number;
  rot: number;
  vr: number;
  gravity: number;
  fade: number;
}

function circleTex(color: number, inner = 1): Texture {
  const s = 24;
  const c = document.createElement('canvas');
  c.width = s;
  c.height = s;
  const ctx = c.getContext('2d')!;
  const r = ((color >> 16) & 255);
  const g = ((color >> 8) & 255);
  const b = color & 255;
  const gr = ctx.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  gr.addColorStop(0, `rgba(${r},${g},${b},${inner})`);
  gr.addColorStop(0.4, `rgba(${r},${g},${b},0.55)`);
  gr.addColorStop(1, `rgba(${r},${g},${b},0)`);
  ctx.fillStyle = gr;
  ctx.fillRect(0, 0, s, s);
  const tex = Texture.from(c);
  tex.source.scaleMode = 'linear';
  return tex;
}

const TEX = {
  fire: circleTex(0xffc878, 1),
  spark: circleTex(0xffe8a0, 1),
  smoke: circleTex(0x6a6058, 0.7),
  dust: circleTex(0xb8a078, 0.55),
  green: circleTex(0x88e060, 0.8),
  purple: circleTex(0xb080ff, 0.85),
  cyan: circleTex(0x70e0e8, 0.8),
  red: circleTex(0xff6a4a, 0.85),
  gold: circleTex(0xe8d060, 0.9),
};

export class ParticleFx {
  readonly layer = new Container();
  private live: P[] = [];
  private pool: Sprite[] = [];
  private time = 0;

  private take(tex: Texture): Sprite {
    const s = this.pool.pop() ?? new Sprite();
    s.texture = tex;
    s.anchor.set(0.5);
    s.visible = true;
    s.alpha = 1;
    s.rotation = 0;
    this.layer.addChild(s);
    return s;
  }

  private spawn(
    tex: Texture,
    x: number,
    y: number,
    vx: number,
    vy: number,
    life: number,
    size: number,
    extra?: Partial<P>,
  ): void {
    if (this.live.length > 900) return;
    this.live.push({
      spr: this.take(tex),
      x,
      y,
      vx,
      vy,
      life,
      max: life,
      size,
      rot: Math.random() * 6,
      vr: (Math.random() - 0.5) * 4,
      gravity: 0,
      fade: 1,
      ...extra,
    });
  }

  muzzle(x: number, y: number, dx: number, dy: number): void {
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    this.spawn(TEX.fire, x + ux * 10, y + uy * 6, ux * 40, uy * 24, 0.08, 14);
    for (let i = 0; i < 4; i++) {
      this.spawn(
        TEX.spark,
        x + ux * 8,
        y + uy * 5,
        ux * 80 + (Math.random() - 0.5) * 50,
        uy * 50 + (Math.random() - 0.5) * 40,
        0.12 + Math.random() * 0.08,
        6 + Math.random() * 5,
        { gravity: 30 },
      );
    }
  }

  /** 炮弹曳光：短命亮点，跟在弹体后面 */
  tracer(x: number, y: number): void {
    this.spawn(TEX.spark, x, y, 0, -4, 0.1, 8, { fade: 0.75 });
  }

  /** 弹着点：比单位阵亡爆炸更小 */
  impact(x: number, y: number, power = 0.35): void {
    const n = 4 + (power * 6) | 0;
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = (28 + Math.random() * 55) * power;
      this.spawn(TEX.fire, x, y, Math.cos(a) * sp, Math.sin(a) * sp * 0.5, 0.16 + Math.random() * 0.12, (7 + Math.random() * 8) * power);
    }
    this.spawn(TEX.smoke, x, y - 2, (Math.random() - 0.5) * 12, -10 - Math.random() * 12, 0.28, 10 * power, { gravity: -8 });
  }

  explosion(x: number, y: number, power = 1): void {
    const n = 8 + (power * 6) | 0;
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = (40 + Math.random() * 90) * power;
      this.spawn(TEX.fire, x, y, Math.cos(a) * sp, Math.sin(a) * sp * 0.55, 0.25 + Math.random() * 0.2, (10 + Math.random() * 14) * power);
    }
    for (let i = 0; i < 5; i++) {
      this.spawn(TEX.smoke, x, y - 4, (Math.random() - 0.5) * 30, -20 - Math.random() * 30, 0.55 + Math.random() * 0.4, 16 * power, {
        gravity: -12,
      });
    }
  }

  dust(x: number, y: number): void {
    this.spawn(TEX.dust, x, y + 4, (Math.random() - 0.5) * 18, -8 - Math.random() * 10, 0.35, 8, { gravity: -6 });
  }

  lawAmbient(lawId: number, camX: number, camY: number, viewW: number, viewH: number, zoom: number): void {
    const budget = 3;
    const hw = viewW / (2 * zoom);
    const hh = viewH / (2 * zoom);
    for (let i = 0; i < budget; i++) {
      const x = camX + (Math.random() - 0.5) * hw * 2.2;
      const y = camY + (Math.random() - 0.5) * hh * 2.2;
      switch (lawId) {
        case 2: // 辐射
          this.spawn(TEX.green, x, y, (Math.random() - 0.5) * 12, -18 - Math.random() * 20, 1.4, 7, { gravity: -8 });
          break;
        case 3: // 引力
          this.spawn(TEX.dust, x, y + 20, (Math.random() - 0.5) * 8, -22 - Math.random() * 16, 1.6, 9, { gravity: -14 });
          break;
        case 8: // 崩塌
          this.spawn(TEX.red, x, y, (Math.random() - 0.5) * 40, (Math.random() - 0.5) * 20, 0.7, 8);
          break;
        case 9: // 电磁
          this.spawn(TEX.cyan, x, y, (Math.random() - 0.5) * 80, (Math.random() - 0.5) * 40, 0.25, 5);
          break;
        case 13: // 生命
          this.spawn(TEX.green, x, y, (Math.random() - 0.5) * 10, -12, 1.2, 6, { gravity: -4 });
          break;
        case 15: // 玄镜
          this.spawn(TEX.gold, x, y, (Math.random() - 0.5) * 6, -8, 1.8, 8, { gravity: -3 });
          break;
        case 7:
        case 14:
          this.spawn(TEX.purple, x, y, (Math.random() - 0.5) * 10, -10, 1.3, 6);
          break;
        default:
          break;
      }
    }
  }

  update(dt: number): void {
    this.time += dt;
    for (let i = this.live.length - 1; i >= 0; i--) {
      const p = this.live[i]!;
      p.life -= dt;
      if (p.life <= 0) {
        this.layer.removeChild(p.spr);
        p.spr.visible = false;
        this.pool.push(p.spr);
        this.live.splice(i, 1);
        continue;
      }
      p.vy += p.gravity * dt;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rot += p.vr * dt;
      const k = p.life / p.max;
      p.spr.position.set(p.x, p.y);
      p.spr.scale.set((p.size / 24) * (0.6 + 0.4 * k));
      p.spr.alpha = k * p.fade;
      p.spr.rotation = p.rot;
    }
  }
}
